import {Button} from '@dabafinance/react-components';
import {Input, Select, Table, Tag} from 'antd';
import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {
  GET_NOTIFICATION_BUCKETS,
  GET_NOTIFICATION_WORKFLOWS,
} from 'graphql/queries/notificationOrchestrator';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import WorkflowForm from './WorkflowForm';
import styles from './workflowOrchestrator.module.scss';

const PRIORITY_COLOR = {
  STANDARD: 'blue',
  HIGH: 'orange',
  CRITICAL: 'red',
};

const Workflows = () => {
  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState(undefined);
  const [priorityFilter, setPriorityFilter] = useState(undefined);
  const [activeFilter, setActiveFilter] = useState(undefined);
  const [modal, setModal] = useState({open: false, workflow: null});

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.NotificationOrchestrator),
  );

  const {data: bucketsData} = useQuery(GET_NOTIFICATION_BUCKETS, {
    variables: {input: {}},
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const {data, loading, refetch} = useQuery(GET_NOTIFICATION_WORKFLOWS, {
    variables: {input: {}},
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const buckets = bucketsData?.getNotificationBuckets?.data || [];
  const workflows = data?.getNotificationWorkflows?.data || [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return workflows.filter(w => {
      if (bucketFilter && w.bucket !== bucketFilter) return false;
      if (priorityFilter && w.priority !== priorityFilter) return false;
      if (activeFilter === 'ACTIVE' && !w.active) return false;
      if (activeFilter === 'INACTIVE' && w.active) return false;
      if (!term) return true;
      return (
        w.workflowKey?.toLowerCase().includes(term) ||
        w.name?.toLowerCase().includes(term) ||
        w.description?.toLowerCase().includes(term)
      );
    });
  }, [workflows, search, bucketFilter, priorityFilter, activeFilter]);

  const columns = [
    {title: 'Workflow key', dataIndex: 'workflowKey', key: 'workflowKey'},
    {title: 'Name', dataIndex: 'name', key: 'name'},
    {
      title: 'Bucket',
      dataIndex: 'bucket',
      key: 'bucket',
      render: bucketKey => <Tag color="purple">{bucketKey || '—'}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: p => <Tag color={PRIORITY_COLOR[p] || 'default'}>{p}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: active => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (_, record) => (
        <Button
          label="Edit"
          type="secondary"
          disabled={!tabOperations?.update}
          onClick={() => setModal({open: true, workflow: record})}
        />
      ),
    },
  ];

  return (
    <div>
      <div className={styles.filters}>
        <Input
          allowClear
          placeholder="Search by key, name or description"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{width: 280}}
        />
        <Select
          allowClear
          placeholder="Bucket"
          value={bucketFilter}
          onChange={setBucketFilter}
          style={{width: 200}}
          showSearch>
          {buckets.map(b => (
            <Select.Option key={b.key} value={b.key}>
              {b.name}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Priority"
          value={priorityFilter}
          onChange={setPriorityFilter}
          style={{width: 160}}>
          <Select.Option value="STANDARD">Standard</Select.Option>
          <Select.Option value="HIGH">High</Select.Option>
          <Select.Option value="CRITICAL">Critical</Select.Option>
        </Select>
        <Select
          allowClear
          placeholder="Status"
          value={activeFilter}
          onChange={setActiveFilter}
          style={{width: 140}}>
          <Select.Option value="ACTIVE">Active</Select.Option>
          <Select.Option value="INACTIVE">Inactive</Select.Option>
        </Select>
        <Button
          label="New Workflow"
          className={styles['button-style']}
          disabled={!tabOperations?.create || buckets.length === 0}
          onClick={() => setModal({open: true, workflow: null})}
        />
      </div>

      <div className={styles.body}>
        <Table
          rowKey={record => record.id || record.workflowKey}
          loading={loading}
          dataSource={filtered}
          columns={columns}
          pagination={{pageSize: 10, showSizeChanger: false}}
        />
      </div>

      <DBModal
        isOpen={modal.open}
        handleClose={() => setModal({open: false, workflow: null})}
        width={620}
        content={
          <WorkflowForm
            workflow={modal.workflow}
            buckets={buckets}
            onClose={() => setModal({open: false, workflow: null})}
            onSaved={() => refetch()}
          />
        }
      />
    </div>
  );
};

export default Workflows;
